import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useFirebase } from "../../../context/FirebaseContext";
import { useLanguage } from "../../../context/LanguageContext";
import {
    getStoreCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    getLocalizedCategoryName,
} from "../../../firebase/category-functions";
import { uploadFile, uploadFileAlternative, DEFAULT_STORE_ID } from "../../../firebase/firebase";
import "./AdminCategories.css";

// Categories List Component
const AdminCategoriesList = () => {
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Translations for the list view
    const translations = {
        en: {
            categoriesManagement: "Categories Management",
            manageDescription: "Manage your store categories to organize products effectively",
            addCategory: "Add New Category",
            searchPlaceholder: "Search categories...",
            noCategories: "No categories found",
            noCategoriesDesc: "Start by creating your first category to organize your products.",
            active: "Active",
            inactive: "Inactive",
            edit: "Edit",
            delete: "Delete",
            confirmDelete: "Delete Category",
            confirmDeleteDesc: "Are you sure you want to delete this category? This action cannot be undone.",
            cancel: "Cancel",
            deleteBtn: "Delete",
            deleting: "Deleting...",
            categoryDeleted: "Category deleted successfully",
            loadingCategories: "Loading categories...",
            errorLoading: "Error loading categories",
            noImage: "No image",
            products: "products"
        },
        ar: {
            categoriesManagement: "إدارة الفئات",
            manageDescription: "قم بإدارة فئات متجرك لتنظيم المنتجات بفعالية",
            addCategory: "إضافة فئة جديدة",
            searchPlaceholder: "البحث في الفئات...",
            noCategories: "لم يتم العثور على فئات",
            noCategoriesDesc: "ابدأ بإنشاء أول فئة لتنظيم منتجاتك.",
            active: "نشط",
            inactive: "غير نشط",
            edit: "تعديل",
            delete: "حذف",
            confirmDelete: "حذف الفئة",
            confirmDeleteDesc: "هل أنت متأكد من أنك تريد حذف هذه الفئة؟ لا يمكن التراجع عن هذا الإجراء.",
            cancel: "إلغاء",
            deleteBtn: "حذف",
            deleting: "جاري الحذف...",
            categoryDeleted: "تم حذف الفئة بنجاح",
            loadingCategories: "جاري تحميل الفئات...",
            errorLoading: "خطأ في تحميل الفئات",
            noImage: "لا توجد صورة",
            products: "منتجات"
        }
    };

    const t = translations[language] || translations.en;

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        if (!DEFAULT_STORE_ID) return;

        try {
            setLoading(true);
            const categoriesData = await getStoreCategories(DEFAULT_STORE_ID);
            setCategories(categoriesData);
        } catch (err) {
            console.error("Error loading categories:", err);
            setError(t.errorLoading);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (categoryId) => {
        navigate(`/admin/categories/edit/${categoryId}`);
    };

    const handleDelete = async (categoryId) => {
        if (!deleteConfirm) return;

        try {
            setDeleting(true);
            await deleteCategory(DEFAULT_STORE_ID, categoryId);
            setCategories(prev => prev.filter(cat => cat.id !== categoryId));
            setDeleteConfirm(null);
            // Show success message
            setTimeout(() => {
                // You can add a toast notification here
            }, 100);
        } catch (err) {
            console.error("Error deleting category:", err);
            setError("Failed to delete category. Please try again.");
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <div className="admin-loading">
                <div className="loading-spinner"></div>
                <h3>{t.loadingCategories}</h3>
            </div>
        );
    }

    return (
        <div className="admin-categories-list">
            <div className="page-header">
                <div className="header-content">
                    <h1>{t.categoriesManagement}</h1>
                    <p className="header-subtitle">{t.manageDescription}</p>
                </div>
                <div className="header-actions">
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate('/admin/categories/new')}
                    >
                        <span className="btn-icon">+</span>
                        {t.addCategory}
                    </button>
                </div>
            </div>

            {error && (
                <div className="alert alert-error">
                    <span className="alert-icon">⚠️</span>
                    <span>{error}</span>
                </div>
            )}

            {categories.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">📁</div>
                    <h3>{t.noCategories}</h3>
                    <p>{t.noCategoriesDesc}</p>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate('/admin/categories/new')}
                    >
                        {t.addCategory}
                    </button>
                </div>
            ) : (
                <div className="categories-grid">
                    {categories.map((category) => (
                        <div key={category.id} className="category-card">
                            <div className="category-image">
                                {category.featuredImage ? (
                                    <img src={category.featuredImage} alt={getLocalizedCategoryName(category, language)} />
                                ) : (
                                    <div className="no-image-placeholder">
                                        <span>📁</span>
                                        <small>{t.noImage}</small>
                                    </div>
                                )}
                            </div>

                            <div className="category-content">
                                <div className="category-header">
                                    <h3 className="category-name">
                                        {getLocalizedCategoryName(category, language)}
                                    </h3>
                                    <span className={`status-badge ${category.status?.toLowerCase()}`}>
                                        {category.status === 'active' ? t.active : t.inactive}
                                    </span>
                                </div>

                                {category.description && (
                                    <p className="category-description">
                                        {category.multilingual?.[language]?.description || category.description}
                                    </p>
                                )}


                            </div>

                            <div className="category-actions">
                                <button
                                    className="btn btn-small btn-outline"
                                    onClick={() => handleEdit(category.id)}
                                >
                                    ✏️ {t.edit}
                                </button>
                                <button
                                    className="btn btn-small btn-danger"
                                    onClick={() => setDeleteConfirm(category.id)}
                                >
                                    🗑️ {t.delete}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h3>{t.confirmDelete}</h3>
                        </div>
                        <div className="modal-body">
                            <p>{t.confirmDeleteDesc}</p>
                            <p><strong>{getLocalizedCategoryName(categories.find(c => c.id === deleteConfirm), language)}</strong></p>
                        </div>
                        <div className="modal-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setDeleteConfirm(null)}
                                disabled={deleting}
                            >
                                {t.cancel}
                            </button>
                            <button
                                className="btn btn-danger"
                                onClick={() => handleDelete(deleteConfirm)}
                                disabled={deleting}
                            >
                                {deleting ? t.deleting : t.deleteBtn}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Category Editor Component
const AdminCategoryEditor = () => {
    const { categoryId } = useParams();
    const navigate = useNavigate();
    const { storeInfo } = useFirebase();
    const { language, availableLanguages } = useLanguage();
    const fileInputRef = useRef(null);
    const isEditMode = !!categoryId;

    // Add active language tab state
    const [activeLanguageTab, setActiveLanguageTab] = useState('en');

    const [category, setCategory] = useState({
        name: "",
        description: "",
        status: "active",
        featuredImage: null,
        multilingual: {
            en: { name: "", description: "" },
            ar: { name: "", description: "" }
        }
    });

    // Translations for admin interface
    const translations = {
        en: {
            editCategory: "Edit Category",
            createNewCategory: "Create New Category",
            updateCategoryInfo: "Update your category information and settings",
            addNewCategory: "Add a new category to organize your products",
            cancel: "Cancel",
            updateCategory: "Update Category",
            createCategory: "Create Category",
            updating: "Updating...",
            creating: "Creating...",
            basicInformation: "Basic Information",
            categoryName: "Category Name",
            categoryNamePlaceholder: "e.g., Electronics, Clothing, Books...",
            description: "Description",
            descriptionPlaceholder: "Provide a brief description of this category (optional)",
            status: "Status",
            activeVisible: "Active - Visible to customers",
            inactiveHidden: "Inactive - Hidden from customers",
            featuredImage: "Featured Image",
            featuredImageDesc: "Add an attractive image to represent this category. This will be displayed on your storefront and category pages.",
            changeImage: "Change Image",
            remove: "Remove",
            english: "English",
            arabic: "Arabic",
            multilingualContent: "Multilingual Content",
            multilingualDesc: "Provide category information in multiple languages to serve your diverse customer base.",
            characters: "characters",
            loadingCategory: "Loading category data...",
            pleaseWait: "Please wait while we fetch the category information.",
            categoryUpdated: "Category updated successfully! ✨",
            categoryCreated: "Category created successfully! 🎉"
        },
        ar: {
            editCategory: "تعديل الفئة",
            createNewCategory: "إنشاء فئة جديدة",
            updateCategoryInfo: "تحديث معلومات وإعدادات الفئة",
            addNewCategory: "إضافة فئة جديدة لتنظيم منتجاتك",
            cancel: "إلغاء",
            updateCategory: "تحديث الفئة",
            createCategory: "إنشاء الفئة",
            updating: "جاري التحديث...",
            creating: "جاري الإنشاء...",
            basicInformation: "المعلومات الأساسية",
            categoryName: "اسم الفئة",
            categoryNamePlaceholder: "مثل: إلكترونيات، ملابس، كتب...",
            description: "الوصف",
            descriptionPlaceholder: "قدم وصفاً موجزاً لهذه الفئة (اختياري)",
            status: "الحالة",
            activeVisible: "نشط - مرئي للعملاء",
            inactiveHidden: "غير نشط - مخفي عن العملاء",
            featuredImage: "الصورة المميزة",
            featuredImageDesc: "أضف صورة جذابة لتمثيل هذه الفئة. ستظهر في واجهة متجرك وصفحات الفئات.",
            changeImage: "تغيير الصورة",
            remove: "إزالة",
            english: "الإنجليزية",
            arabic: "العربية",
            multilingualContent: "المحتوى متعدد اللغات",
            multilingualDesc: "قدم معلومات الفئة بلغات متعددة لخدمة قاعدة عملائك المتنوعة.",
            characters: "حرف",
            loadingCategory: "جاري تحميل بيانات الفئة...",
            pleaseWait: "يرجى الانتظار بينما نحضر معلومات الفئة.",
            categoryUpdated: "تم تحديث الفئة بنجاح! ✨",
            categoryCreated: "تم إنشاء الفئة بنجاح! 🎉"
        }
    };

    const t = translations[language] || translations.en;

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isDragOver, setIsDragOver] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [showToast, setShowToast] = useState(false);

    // Load category data if in edit mode
    useEffect(() => {
        const fetchCategory = async () => {
            if (!isEditMode) return;

            try {
                setLoading(true);
                const categories = await getStoreCategories(DEFAULT_STORE_ID);
                const categoryData = categories.find(cat => cat.id === categoryId);

                if (categoryData) {
                    // Ensure multilingual structure exists
                    const updatedCategoryData = {
                        ...categoryData,
                        multilingual: categoryData.multilingual || {
                            en: {
                                name: categoryData.name || '',
                                description: categoryData.description || ''
                            },
                            ar: {
                                name: categoryData.nameAr || '',
                                description: categoryData.descriptionAr || ''
                            }
                        }
                    };

                    setCategory(updatedCategoryData);
                    if (categoryData.featuredImage) {
                        setPreviewUrl(categoryData.featuredImage);
                    }
                } else {
                    setError("Category not found");
                    setTimeout(() => navigate("/admin/categories"), 2000);
                }
            } catch (err) {
                console.error("Error fetching category:", err);
                setError("Failed to load category data. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchCategory();
    }, [categoryId, isEditMode, navigate, storeInfo]);

    // Track unsaved changes
    useEffect(() => {
        const hasChanges = category.name || category.description || selectedFile;
        setUnsavedChanges(hasChanges && !saving);
    }, [category, selectedFile, saving]);

    // Warn about unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (unsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [unsavedChanges]);

    // Handle form field changes with validation
    const handleChange = (e) => {
        const { name, value } = e.target;

        // Clear field-specific error when user starts typing
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }

        // Clear general error
        if (error) setError("");

        // Handle multilingual fields
        if (name.includes('_')) {
            const [field, lang] = name.split('_');
            setCategory(prev => ({
                ...prev,
                multilingual: {
                    ...prev.multilingual,
                    [lang]: {
                        ...prev.multilingual[lang],
                        [field]: value
                    }
                }
            }));

            // Also update the main field if it's English
            if (lang === 'en') {
                setCategory(prev => ({
                    ...prev,
                    [field]: value
                }));
            }
        } else {
            setCategory(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Validate individual field
    const validateField = (name, value) => {
        const fieldName = name.includes('_') ? name.split('_')[0] : name;

        switch (fieldName) {
            case 'name':
                if (!value.trim()) return "Category name is required";
                if (value.trim().length < 2) return "Category name must be at least 2 characters";
                if (value.trim().length > 50) return "Category name must be less than 50 characters";
                return "";
            case 'description':
                if (value && value.length > 500) return "Description must be less than 500 characters";
                return "";
            default:
                return "";
        }
    };

    // Handle real-time field validation
    const handleBlur = (e) => {
        const { name, value } = e.target;
        const error = validateField(name, value);

        setFieldErrors(prev => ({
            ...prev,
            [name]: error
        }));
    };

    // Handle drag and drop for images
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelection(files[0]);
        }
    };

    // Handle image file selection with enhanced validation
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            handleFileSelection(file);
        }
    };

    const handleFileSelection = (file) => {
        // Reset previous errors
        setError("");
        setFieldErrors(prev => ({ ...prev, image: "" }));

        // Enhanced file validation
        const maxSize = 5 * 1024 * 1024; // 5MB
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

        if (!allowedTypes.includes(file.type)) {
            setFieldErrors(prev => ({
                ...prev,
                image: "Please select a valid image file (JPEG, PNG, or WebP)"
            }));
            return;
        }

        if (file.size > maxSize) {
            setFieldErrors(prev => ({
                ...prev,
                image: "Image file is too large. Maximum size is 5MB."
            }));
            return;
        }

        setSelectedFile(file);

        // Create preview with better error handling
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result);
        };
        reader.onerror = () => {
            setFieldErrors(prev => ({
                ...prev,
                image: "Failed to read image file"
            }));
        };
        reader.readAsDataURL(file);
    };

    // Remove the selected image with confirmation
    const handleRemoveImage = () => {
        if (isEditMode && category.featuredImage && !selectedFile) {
            // Confirm removal of existing image
            if (!window.confirm("Are you sure you want to remove the current image?")) {
                return;
            }
        }

        setSelectedFile(null);
        setPreviewUrl(null);
        setFieldErrors(prev => ({ ...prev, image: "" }));

        // Clear file input
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }

        // Only in edit mode, if we're removing an existing image
        if (isEditMode && category.featuredImage) {
            setCategory(prev => ({
                ...prev,
                featuredImage: null
            }));
        }
    };

    // Comprehensive form validation
    const validateForm = () => {
        const errors = {};

        // Validate English fields (required)
        const enNameError = validateField('name', category.multilingual?.en?.name || '');
        if (enNameError) errors['name_en'] = enNameError;

        const enDescError = validateField('description', category.multilingual?.en?.description || '');
        if (enDescError) errors['description_en'] = enDescError;

        // Validate Arabic fields (optional but validate if provided)
        const arNameError = validateField('name', category.multilingual?.ar?.name || '');
        if (arNameError && category.multilingual?.ar?.name) errors['name_ar'] = arNameError;

        const arDescError = validateField('description', category.multilingual?.ar?.description || '');
        if (arDescError && category.multilingual?.ar?.description) errors['description_ar'] = arDescError;

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form submission with better UX
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            setError("Please fix the errors above before saving.");
            return;
        }

        setSaving(true);
        setError("");
        setUploadProgress(0);

        try {
            let imageUrl = category.featuredImage;

            // If a new file was selected, upload it with progress
            if (selectedFile) {
                // Create a more specific path for categories
                const timestamp = Date.now();
                const cleanFileName = selectedFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
                const path = `categories/${timestamp}_${cleanFileName}`;

                // Simulate upload progress (replace with actual progress if available)
                const progressInterval = setInterval(() => {
                    setUploadProgress(prev => Math.min(prev + 10, 90));
                }, 100);

                try {
                    // console.log(`🔵 Uploading file to: stores/${DEFAULT_STORE_ID}/${path}`);

                    // Try alternative upload method first (better error handling)
                    try {
                        imageUrl = await uploadFileAlternative(DEFAULT_STORE_ID, path, selectedFile);
                        // console.log(`✅ Alternative upload successful, URL: ${imageUrl}`);
                    } catch (altError) {
                        console.warn(`⚠️ Alternative upload failed, trying standard method:`, altError);
                        // Fallback to original upload method
                        imageUrl = await uploadFile(DEFAULT_STORE_ID, path, selectedFile);
                        console.log(`✅ Standard upload successful, URL: ${imageUrl}`);
                    }

                    setUploadProgress(100);
                    clearInterval(progressInterval);
                    // console.log(`🎉 File uploaded successfully, URL: ${imageUrl}`);
                } catch (uploadError) {
                    clearInterval(progressInterval);
                    console.error("❌ Upload error:", uploadError);
                    throw new Error(`Image upload failed: ${uploadError.message}`);
                }
            }

            // Prepare category data with multilingual support
            const categoryData = {
                name: category.multilingual?.en?.name?.trim() || category.name?.trim() || '',
                description: category.multilingual?.en?.description?.trim() || category.description?.trim() || '',
                status: category.status,
                featuredImage: imageUrl,
                multilingual: {
                    en: {
                        name: category.multilingual?.en?.name?.trim() || category.name?.trim() || '',
                        description: category.multilingual?.en?.description?.trim() || category.description?.trim() || ''
                    },
                    ar: {
                        name: category.multilingual?.ar?.name?.trim() || '',
                        description: category.multilingual?.ar?.description?.trim() || ''
                    }
                }
            };

            // console.log("Saving category data:", categoryData);

            if (isEditMode) {
                await updateCategory(DEFAULT_STORE_ID, categoryId, categoryData);
                setSuccessMessage(t.categoryUpdated || "Category updated successfully! ✨");
            } else {
                await createCategory(DEFAULT_STORE_ID, categoryData);
                setSuccessMessage(t.categoryCreated || "Category created successfully! 🎉");
            }

            // Success feedback
            setUnsavedChanges(false);
            setShowToast(true);

            // Auto-hide success message and redirect after 2.5 seconds
            setTimeout(() => {
                setShowSuccess(false);
            }, 2000);

            setTimeout(() => {
                setShowToast(false);
                navigate("/admin/categories");
            }, 2500);

        } catch (err) {
            console.error("Error saving category:", err);
            setError(`Failed to ${isEditMode ? 'update' : 'create'} category: ${err.message}`);
            setUploadProgress(0);
        } finally {
            setSaving(false);
        }
    };

    // Handle navigation with unsaved changes warning
    const handleCancel = () => {
        if (unsavedChanges) {
            if (window.confirm("You have unsaved changes. Are you sure you want to leave?")) {
                navigate("/admin/categories");
            }
        } else {
            navigate("/admin/categories");
        }
    };

    if (loading) {
        return (
            <div className="admin-loading">
                <div className="loading-spinner"></div>
                <h3>{t.loadingCategory}</h3>
                <p>{t.pleaseWait}</p>
            </div>
        );
    }

    return (
        <div className="admin-category-editor">
            <div className="editor-header">
                <div className="header-content">
                    <h1>
                        {isEditMode ? t.editCategory : t.createNewCategory}
                        {unsavedChanges && <span className="unsaved-indicator">*</span>}
                    </h1>
                    <p className="header-subtitle">
                        {isEditMode
                            ? t.updateCategoryInfo
                            : t.addNewCategory
                        }
                    </p>
                </div>
                <div className="editor-actions">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCancel}
                        disabled={saving}
                    >
                        {t.cancel}
                    </button>
                    <button
                        type="submit"
                        form="category-form"
                        className="btn btn-primary"
                        disabled={saving || Object.keys(fieldErrors).some(key => fieldErrors[key])}
                    >
                        {saving ? (
                            <>
                                <span className="btn-spinner"></span>
                                {uploadProgress > 0 && uploadProgress < 100
                                    ? `Uploading... ${uploadProgress}%`
                                    : isEditMode ? t.updating : t.creating
                                }
                            </>
                        ) : (
                            <>
                                {isEditMode ? t.updateCategory : t.createCategory}
                            </>
                        )}
                    </button>
                </div>
            </div>

            {error && (
                <div className="alert alert-error">
                    <span className="alert-icon">⚠️</span>
                    <span>{error}</span>
                </div>
            )}

            {showSuccess && (
                <div className="alert alert-success">
                    <span className="alert-icon">✅</span>
                    <span>{successMessage}</span>
                    <div className="success-progress-bar">
                        <div className="success-progress-fill"></div>
                    </div>
                </div>
            )}

            <form id="category-form" className="editor-form" onSubmit={handleSubmit}>
                <div className="form-sections">
                    {/* Multilingual Content Section */}
                    <div className="form-section">
                        <h2 className="section-title">{t.multilingualContent}</h2>
                        <p className="section-description">
                            {t.multilingualDesc}
                        </p>

                        {/* Language Tabs */}
                        <div className="language-tabs">
                            {availableLanguages.map((lang) => (
                                <button
                                    key={lang.code}
                                    type="button"
                                    className={`language-tab ${activeLanguageTab === lang.code ? 'active' : ''}`}
                                    onClick={() => setActiveLanguageTab(lang.code)}
                                >
                                    {lang.code === 'en' ? t.english : t.arabic}
                                </button>
                            ))}
                        </div>

                        {/* Language-specific form fields */}
                        <div className="language-content">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label htmlFor={`name_${activeLanguageTab}`} className="form-label required">
                                        {t.categoryName} ({activeLanguageTab === 'en' ? t.english : t.arabic})
                                    </label>
                                    <input
                                        type="text"
                                        id={`name_${activeLanguageTab}`}
                                        name={`name_${activeLanguageTab}`}
                                        value={category.multilingual[activeLanguageTab]?.name || ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder={t.categoryNamePlaceholder}
                                        className={`form-input ${fieldErrors[`name_${activeLanguageTab}`] ? 'error' : ''}`}
                                        disabled={saving}
                                        maxLength={50}
                                        required={activeLanguageTab === 'en'}
                                    />
                                    {fieldErrors[`name_${activeLanguageTab}`] && (
                                        <span className="field-error">{fieldErrors[`name_${activeLanguageTab}`]}</span>
                                    )}
                                    <span className="field-hint">
                                        {(category.multilingual[activeLanguageTab]?.name || '').length}/50 {t.characters}
                                    </span>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="status" className="form-label">
                                        {t.status}
                                    </label>
                                    <select
                                        id="status"
                                        name="status"
                                        value={category.status}
                                        onChange={handleChange}
                                        className="form-select"
                                        disabled={saving}
                                    >
                                        <option value="Active">{t.activeVisible}</option>
                                        <option value="Inactive">{t.inactiveHidden}</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor={`description_${activeLanguageTab}`} className="form-label">
                                    {t.description} ({activeLanguageTab === 'en' ? t.english : t.arabic})
                                </label>
                                <textarea
                                    id={`description_${activeLanguageTab}`}
                                    name={`description_${activeLanguageTab}`}
                                    value={category.multilingual[activeLanguageTab]?.description || ''}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder={t.descriptionPlaceholder}
                                    className={`form-textarea ${fieldErrors[`description_${activeLanguageTab}`] ? 'error' : ''}`}
                                    rows="4"
                                    disabled={saving}
                                    maxLength={500}
                                />
                                {fieldErrors[`description_${activeLanguageTab}`] && (
                                    <span className="field-error">{fieldErrors[`description_${activeLanguageTab}`]}</span>
                                )}
                                <span className="field-hint">
                                    {(category.multilingual[activeLanguageTab]?.description || '').length}/500 {t.characters}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Featured Image Section */}
                    <div className="form-section">
                        <h2 className="section-title">{t.featuredImage}</h2>
                        <p className="section-description">
                            {t.featuredImageDesc}
                        </p>

                        <div className="image-upload-section">
                            {previewUrl ? (
                                <div className="image-preview-container">
                                    <div className="image-preview">
                                        <img
                                            src={previewUrl}
                                            alt="Category preview"
                                            className="preview-image"
                                        />
                                        <div className="image-overlay">
                                            <button
                                                type="button"
                                                className="btn btn-small btn-secondary"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={saving}
                                            >
                                                {t.changeImage}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-small btn-danger"
                                                onClick={handleRemoveImage}
                                                disabled={saving}
                                            >
                                                {t.remove}
                                            </button>
                                        </div>
                                    </div>
                                    {selectedFile && (
                                        <div className="file-info">
                                            <p><strong>Selected:</strong> {selectedFile.name}</p>
                                            <p><strong>Size:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div
                                    className={`image-dropzone ${isDragOver ? 'drag-over' : ''}`}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        id="featuredImage"
                                        name="featuredImage"
                                        onChange={handleFileChange}
                                        accept="image/jpeg,image/jpg,image/png,image/webp"
                                        className="file-input-hidden"
                                        disabled={saving}
                                    />
                                    <div className="dropzone-content">
                                        <div className="upload-icon">📷</div>
                                        <h3>Upload Category Image</h3>
                                        <p>Drag and drop an image here, or click to select</p>
                                        <button
                                            type="button"
                                            className="btn btn-outline"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={saving}
                                        >
                                            Choose File
                                        </button>
                                    </div>
                                </div>
                            )}

                            {fieldErrors.image && (
                                <div className="field-error">{fieldErrors.image}</div>
                            )}

                            <div className="upload-guidelines">
                                <h4>Image Guidelines:</h4>
                                <ul>
                                    <li>Supported formats: JPEG, PNG, WebP</li>
                                    <li>Maximum file size: 5MB</li>
                                    <li>Recommended dimensions: 1200×800 pixels</li>
                                    <li>Use high-quality images for best results</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </form>

            {/* Toast Notification */}
            {showToast && (
                <div className="toast-notification">
                    <div className="toast-content">
                        <span className="toast-icon">✅</span>
                        <span className="toast-message">{successMessage}</span>
                        <span className="toast-redirect">Redirecting to categories...</span>
                    </div>
                </div>
            )}


        </div>
    );
};

// Main Categories Component that handles routing
const AdminCategories = () => {
    const { categoryId } = useParams();
    const location = useLocation();

    // Check if we're in create mode, edit mode, or list mode
    const isCreateMode = location.pathname.includes('/new');
    const isEditMode = location.pathname.includes('/edit') && categoryId;
    const isListMode = !isCreateMode && !isEditMode;

    if (isListMode) {
        return <AdminCategoriesList />;
    } else {
        return <AdminCategoryEditor />;
    }
};

export default AdminCategories;